/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useEffect, useState } from 'react';
import { ComponentCollectionItem, ComponentType, ParticipantType } from '../../types';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PdfSearchModal from '../bids/PdfSearchModal';
import './ComponentCollectionManager.css';

interface ComponentCollectionManagerProps {
  collectionId: number;
}

const ComponentCollectionManager: React.FC<ComponentCollectionManagerProps> = ({ collectionId }) => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<ComponentCollectionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingCount, setEditingCount] = useState<number>(1);

  const isManager = currentUser?.type === ParticipantType.MANAGER || currentUser?.type === ParticipantType.ADMIN;

  useEffect(() => {
    loadCollectionItems();
  }, [collectionId]);

  const loadCollectionItems = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.getCollectionItems(collectionId);
      if (response.success) {
        setItems(response.items);
      } else {
        setError('Failed to load collection items');
      }
    } catch (err: any) {
      console.error('Error loading collection items:', err);
      setError(err.message || 'Failed to load collection items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComponent = async (component: any) => {
    try {
      const response = await apiClient.addItemToCollection(collectionId, component.id, 1);
      if (response.success) {
        await loadCollectionItems();
        setShowAddModal(false);
      } else {
        alert('Failed to add component to collection');
      }
    } catch (err: any) {
      console.error('Error adding component:', err);
      alert(err.message || 'Failed to add component to collection');
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!window.confirm('Are you sure you want to remove this component from the collection?')) {
      return;
    }

    try {
      const response = await apiClient.removeItemFromCollection(itemId);
      if (response.success) {
        await loadCollectionItems();
      } else {
        alert('Failed to remove component from collection');
      }
    } catch (err: any) {
      console.error('Error removing component:', err);
      alert(err.message || 'Failed to remove component from collection');
    }
  };

  const handleStartEditCount = (item: ComponentCollectionItem) => {
    setEditingItemId(item.id);
    setEditingCount(item.suggestedCount);
  };

  const handleSaveCount = async (itemId: number) => {
    try {
      const response = await apiClient.updateCollectionItemCount(itemId, editingCount);
      if (response.success) {
        await loadCollectionItems();
        setEditingItemId(null);
      } else {
        alert('Failed to update count');
      }
    } catch (err: any) {
      console.error('Error updating count:', err);
      alert(err.message || 'Failed to update count');
    }
  };

  const handleCancelEditCount = () => {
    setEditingItemId(null);
  };

  const handleMoveUp = async (itemId: number) => {
    try {
      const response = await apiClient.moveCollectionItemUp(itemId);
      if (response.success) {
        await loadCollectionItems();
      }
    } catch (err: any) {
      console.error('Error moving item up:', err);
      alert(err.message || 'Failed to move item up');
    }
  };

  const handleMoveDown = async (itemId: number) => {
    try {
      const response = await apiClient.moveCollectionItemDown(itemId);
      if (response.success) {
        await loadCollectionItems();
      }
    } catch (err: any) {
      console.error('Error moving item down:', err);
      alert(err.message || 'Failed to move item down');
    }
  };

  if (isLoading) {
    return (
      <div className="collection-manager">
        <h3>Collection Items</h3>
        <div className="loading">Loading collection items...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-manager">
        <h3>Collection Items</h3>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Get list of excluded component IDs (components already in the collection)
  const excludedComponentIds = items.map(item => item.component.id);

  return (
    <div className="collection-manager">
      <div className="collection-header">
        <h3>Collection Items ({items.length})</h3>
        {isManager && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary btn-small"
          >
            + Add Component
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <p>No components in this collection yet.</p>
          {isManager && <p>Click "Add Component" to add components to this collection.</p>}
        </div>
      ) : (
        <div className="collection-items-list">
          {items.map((item, index) => (
            <div key={item.id} className="collection-item-card">
              <div className="item-order-badge">#{index + 1}</div>

              <div className="item-content">
                <div className="item-main-info">
                  <h4>{item.component.name}</h4>
                  {item.component.manufacturerName && (
                    <div className="item-meta">
                      {item.component.manufacturerName}
                      {item.component.manufacturerCode && ` | ${item.component.manufacturerCode}`}
                      {item.component.price && ` | $${item.component.price.toFixed(2)}`}
                    </div>
                  )}
                </div>

                <div className="item-count-section">
                  <label>Suggested Count:</label>
                  {editingItemId === item.id ? (
                    <div className="count-edit-controls">
                      <input
                        type="number"
                        min="1"
                        value={editingCount}
                        onChange={(e) => setEditingCount(parseInt(e.target.value) || 1)}
                        className="count-input"
                      />
                      <button
                        onClick={() => handleSaveCount(item.id)}
                        className="btn btn-success btn-small"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditCount}
                        className="btn btn-secondary btn-small"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="count-display">
                      <span className="count-value">{item.suggestedCount}</span>
                      {isManager && (
                        <button
                          onClick={() => handleStartEditCount(item)}
                          className="btn btn-link btn-small"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isManager && (
                <div className="item-actions">
                  <button
                    onClick={() => handleMoveUp(item.id)}
                    disabled={index === 0}
                    className="btn btn-icon"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => handleMoveDown(item.id)}
                    disabled={index === items.length - 1}
                    className="btn btn-icon"
                    title="Move down"
                  >
                    ▼
                  </button>
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="btn btn-danger btn-small"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <PdfSearchModal
          onClose={() => setShowAddModal(false)}
          onSelectComponent={handleAddComponent}
          excludedComponentIds={excludedComponentIds}
        />
      )}
    </div>
  );
};

export default ComponentCollectionManager;
